/**
 * Day · by line.
 *
 * Lanes are the plant's work centers; inside a lane, the steps running that
 * day; inside a step, its crew groups (the labor phases that share an
 * operation and a job role, read as one line — see `groupCrewLines`); inside
 * a group, one card per assignment on the same clock as the worker view. A
 * crew line nobody is on is a dashed "+ Assign" slot. Every group is a drop
 * target (the drop lands on its first short phase); every card is a drag
 * source. A card can also be clicked and then a phase clicked — the
 * fallback goes through the same move call.
 */
import React, { useCallback, useMemo } from "react";

import { MoveGap } from "../../../../../services/roster-assignments-api";
import { hhmm } from "../../../utils/time";
import { ManualMoves } from "../controller/use-manual-moves";
import { Clock } from "../types";
import { PersonDay } from "../utils/day-people";
import { bestPhaseTarget, DropState, phaseDropState } from "../utils/drop-state"; // eslint-disable-line sort-imports
import { groupCrewLines, LaneCard, LaneCrewGroup, LaneRow, LaneSlot, LaneStep } from "../utils/lane-rows"; // eslint-disable-line sort-imports
import { pinnedUntilLabel } from "../utils/pin-range";
import { placeWindow } from "../utils/place-window";
import { eyebrowSx,fmtHours,lbb,ROW_GRID } from "../utils/tokens"; // eslint-disable-line sort-imports
import { MoveTarget } from "../utils/undo";
import { AvailabilityBadge, availabilityStripe, availabilityTitle,OUT_NAME_OPACITY, OutCount } from "./availability-badge"; // eslint-disable-line sort-imports
import { FillRequest } from "./fill-picker";
import { CardMenu, dropSurface, GapPrompt, useCardDrag, useCardDrop, useCardHover, WorkerDragItem } from "./manual-controls"; // eslint-disable-line sort-imports
import { PeopleList, WORKER_LIST_WIDTH } from "./people-list";
import { StepDetails } from "./step-details";

const LANE_GRID = `${ROW_GRID.split(" ")[0]} 1fr`;
const CARD_HEIGHT = 26;
const LANE_GAP = 4;

const pct = (hours: number, clock: Clock): string =>
	`${((hours - clock.start) / (clock.end - clock.start)) * 100}%`;
const widthPct = (start: number, end: number, clock: Clock, min = 0.012): string =>
	`${Math.max((end - start) / (clock.end - clock.start), min) * 100}%`;

/**
 * A card is anchored where its block starts but never shrinks below a readable
 * width (a 30-minute QA check would otherwise be a sliver). The CSS `max()`
 * widens it, and `min()` on the left edge keeps a widened card inside the lane.
 * A widened card says its real length so the width is not mistaken for time.
 */
const MIN_CARD_PX = 170;
const MIN_SLOT_PX = 150;
const readableLeft = (startHours: number, clock: Clock, minPx: number): string =>
	`min(${pct(startHours, clock)}, calc(100% - ${minPx}px))`;
const readableWidth = (start: number, end: number, clock: Clock, minPx: number): string =>
	`max(${widthPct(start, end, clock)}, ${minPx}px)`;
const durationLabel = (start: number, end: number): string => {
	const minutes = Math.round((end - start) * 60);
	return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)}h${minutes % 60 ? ` ${minutes % 60}m` : ""}`;
};
/** True when the block's own width would be narrower than the readable minimum (rough: 10% of a 24 h lane). */
const isWidened = (start: number, end: number, clock: Clock): boolean =>
	(end - start) / (clock.end - clock.start) < 0.1;

// ---------------------------------------------------------------------------

/** yyyy-MM-dd the worker's standing pin on this lane ends, if one covers the day. */
type PinEndFor = (workerId: string) => string | null;
/** The same, for a worker on a named work center. */
export type PinEndOn = (workerId: string, workstation: string) => string | null;

const Card: React.FC<{
	card: LaneCard;
	label: string;
	clock: Clock;
	lane: number;
	selected: boolean;
	moves: ManualMoves;
	onSelect: (id: number | null) => void;
	pinnedUntil: string | null;
}> = ({ card, label, clock, lane, selected, moves, onSelect, pinnedUntil }) => {
	const item = useMemo(
		() => ({
			id: card.id,
			label,
			workerId: card.workerId,
			workerName: card.workerName,
			workOrderId: card.workOrderId,
			phaseId: card.phaseId,
		}),
		[card.id, label, card.workerId, card.workerName, card.workOrderId, card.phaseId]
	);
	const { isDragging, dragRef } = useCardDrag(item, moves);
	const wide = (card.end - card.start) / (clock.end - clock.start) >= 0.08;
	const pinText = card.isManuallyAssigned ? (pinnedUntil ? ` · ${pinnedUntilLabel(pinnedUntil)}` : " · pinned") : "";
	const title = `${card.workerName} · ${card.jobRole}${card.segmentSeq != null ? ` · part ${card.segmentSeq}` : ""} · ${hhmm(card.start)} – ${hhmm(card.end)}${pinText}${availabilityTitle(card.unavailable)}`;
	const toggle = useCallback(() => onSelect(selected ? null : card.id), [onSelect, selected, card.id]);
	const handleClick = useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation();
			toggle();
		},
		[toggle]
	);
	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === "Enter" || event.key === " ") {
				event.preventDefault();
				toggle();
			}
		},
		[toggle]
	);
	const handleUnpin = useCallback(() => void moves.unpin(card.id), [moves, card.id]);
	const handleKeepWeek = useCallback(() => void moves.extend(card.id, "week"), [moves, card.id]);
	const handleKeepUntil = useCallback(() => moves.openUntil(card.id), [moves, card.id]);
	const handleReplace = useCallback(() => moves.replace(card.id), [moves, card.id]);
	const handleRemove = useCallback(() => moves.openRemove(card.id), [moves, card.id]);
	const handleSplit = useCallback(() => void moves.openSplit(card.id), [moves, card.id]);
	const { hover, handlers } = useCardHover();
	return (
		<div
			ref={dragRef}
			role="button"
			tabIndex={0}
			aria-label={title}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			title={title}
			{...handlers}
			style={{
				position: "absolute",
				top: lane * (CARD_HEIGHT + LANE_GAP),
				height: CARD_HEIGHT,
				left: readableLeft(card.start, clock, MIN_CARD_PX),
				width: readableWidth(card.start, card.end, clock, MIN_CARD_PX),
				display: "flex",
				alignItems: "center",
				gap: 6,
				paddingLeft: 8,
				paddingRight: 4,
				boxSizing: "border-box",
				background: selected ? lbb.brick : card.isManuallyAssigned ? lbb.paper : lbb.blockFill,
				border: selected ? lbb.borderInk : `1px solid ${card.isManuallyAssigned ? lbb.navy : lbb.navy20}`,
				color: selected ? lbb.paper : lbb.navy,
				borderRadius: 2,
				fontFamily: lbb.fontUi,
				fontSize: 11,
				fontWeight: 600,
				overflow: "hidden",
				whiteSpace: "nowrap",
				cursor: "grab",
				opacity: isDragging ? 0.35 : 1,
				transition: `background 160ms ${lbb.ease}`,
				...availabilityStripe(card.unavailable),
			}}
		>
			<span style={{ overflow: "hidden", textOverflow: "ellipsis", minWidth: 0, opacity: card.isOut ? OUT_NAME_OPACITY : 1 }}>
				{card.workerName}
			</span>
			{card.unavailable && <AvailabilityBadge row={card.unavailable} inverted={selected} />}
			{isWidened(card.start, card.end, clock) && (
				<span style={{ color: selected ? lbb.brickSoft : lbb.ink60, fontWeight: 400, whiteSpace: "nowrap" }}>
					{durationLabel(card.start, card.end)}
				</span>
			)}
			{wide && (
				<span style={{ color: selected ? lbb.brickSoft : lbb.ink60, fontWeight: 400, overflow: "hidden", textOverflow: "ellipsis" }}>
					{card.jobRole}
					{card.segmentSeq != null ? ` · part ${card.segmentSeq}` : ""}
				</span>
			)}
			<span style={{ marginLeft: "auto", display: "flex" }}>
				<CardMenu
					pinned={card.isManuallyAssigned}
					visible={hover}
					urgent={Boolean(card.unavailable)}
					onUnpin={handleUnpin}
					onKeepWeek={handleKeepWeek}
					onKeepUntil={handleKeepUntil}
					onReplace={handleReplace}
					onRemove={handleRemove}
					onSplit={handleSplit}
					pinnedUntil={pinnedUntil}
					color={selected ? lbb.paper : card.unavailable || card.isManuallyAssigned ? lbb.brick : lbb.ink60}
					size={13}
				/>
			</span>
		</div>
	);
};

/** One open crew line, one shift segment long; opens the fill picker for that window. */
const EmptySlot: React.FC<{ jobRole: string; slot: LaneSlot; clock: Clock; lane: number; onAssign: (slot: LaneSlot) => void }> = ({
	jobRole,
	slot,
	clock,
	lane,
	onAssign,
}) => {
	const handleClick = useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation();
			onAssign(slot);
		},
		[onAssign, slot]
	);
	return (
		<button
			type="button"
			onClick={handleClick}
			title={`${hhmm(slot.start)} – ${hhmm(slot.end)}`}
			style={{
				position: "absolute",
				top: lane * (CARD_HEIGHT + LANE_GAP),
				height: CARD_HEIGHT,
				left: readableLeft(slot.start, clock, MIN_SLOT_PX),
				width: readableWidth(slot.start, slot.end, clock, MIN_SLOT_PX),
				boxSizing: "border-box",
				border: `1.5px dashed ${lbb.navy40}`,
				borderRadius: 2,
				background: "transparent",
				color: lbb.ink60,
				fontFamily: lbb.fontUi,
				fontSize: 11,
				fontWeight: 600,
				cursor: "pointer",
				textAlign: "left",
				paddingLeft: 8,
				whiteSpace: "nowrap",
				overflow: "hidden",
			}}
		>
			+ Assign {jobRole}
		</button>
	);
};

// ---------------------------------------------------------------------------

/** Cards stack by worker (segments of one worker share a lane), then one lane per open slot. */
const laneOf = (group: LaneCrewGroup): Map<string, number> => {
	const lanes = new Map<string, number>();
	group.cards.forEach((card) => {
		if (!lanes.has(card.workerId)) {
			lanes.set(card.workerId, lanes.size);
		}
	});
	return lanes;
};

/** A crew group inside a step: the drop target, its cards on the clock, its open slots. */
const Group: React.FC<{
	step: LaneStep;
	group: LaneCrewGroup;
	clock: Clock;
	selected: number | null;
	moves: ManualMoves;
	onSelect: (id: number | null) => void;
	onFill: (request: FillRequest) => void;
	pinEndFor: PinEndFor;
	/** yyyy-MM-dd of the shown day. */
	dayIso: string;
	workstation: string;
}> = ({ step, group, clock, selected, moves, onSelect, onFill, pinEndFor, dayIso, workstation }) => {
	const droppable = group.dropPhaseId !== null;
	const target = useMemo<MoveTarget>(
		() => ({
			kind: "phase",
			workOrderId: step.workOrderId,
			phaseId: group.dropPhaseId ?? -1,
			label: `${step.title} · ${group.operationName}`,
		}),
		[step.workOrderId, step.title, group.dropPhaseId, group.operationName]
	);
	// The group reads as its most welcoming phase.
	const { targetIndex } = moves;
	const grade = useMemo(
		() => (droppable ? bestPhaseTarget(group.phaseIds.map((id) => targetIndex.phase(step.workOrderId, id))) : undefined),
		[droppable, group.phaseIds, targetIndex, step.workOrderId]
	);
	const targetsLoaded = targetIndex.loaded;
	const state = useMemo<DropState>(
		() => (droppable ? phaseDropState(grade, targetsLoaded) : { ...phaseDropState(undefined), allowed: false }),
		[droppable, grade, targetsLoaded]
	);
	// A name from the people list: one manual block on this step for the shown shift.
	const handlePlace = useCallback(
		(worker: WorkerDragItem) => {
			const window = placeWindow(dayIso, step, group);
			void moves.placeWorker({
				workerId: worker.workerId,
				workerName: worker.workerName,
				workOrderId: step.workOrderId,
				phaseId: window.phaseId,
				jobRoleId: group.jobRoleId,
				jobRoleName: group.jobRole,
				start: window.start,
				end: window.end,
				workstation,
				stepTitle: step.title,
			});
		},
		[moves, dayIso, step, group, workstation]
	);
	const { isOver, canDrop, chipOver, dropRef } = useCardDrop(target, state, moves, undefined, handlePlace);
	// Click-then-click (and its keyboard twin): the picked-up card lands here.
	const receiving = selected !== null && droppable;
	const handoff = useCallback(() => {
		if (selected !== null && droppable && state.allowed) {
			moves.requestMove(selected, target, {
				confirmFirst: state.confirm ? `${state.caption || "Not qualified"} for ${group.jobRole}. Place anyway?` : null,
			});
			onSelect(null);
		}
	}, [selected, droppable, moves, target, state.allowed, state.confirm, state.caption, group.jobRole, onSelect]);
	const onKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (receiving && (event.key === "Enter" || event.key === " ")) {
				event.preventDefault();
				handoff();
			}
		},
		[receiving, handoff]
	);

	const workerLanes = useMemo(() => laneOf(group), [group]);
	const laneCount = workerLanes.size + group.slots.length;
	const height = Math.max(laneCount, 1) * (CARD_HEIGHT + LANE_GAP) - LANE_GAP;
	const dragging = Boolean(moves.dragging);
	// The slot's own window (one shift segment) on the slot's own phase, so an
	// off-shift ask reads "8.5 h", not the whole step.
	const handleAssign = useCallback(
		(slot: LaneSlot) =>
			onFill({
				workOrderId: step.workOrderId,
				phaseId: slot.phaseId,
				start: slot.startIso,
				end: slot.endIso,
				jobRoleId: group.jobRoleId,
				jobRoleName: group.jobRole,
				title: `${step.title} · ${group.operationName} · ${group.jobRole}`,
			}),
		[onFill, step.workOrderId, step.title, group.jobRoleId, group.jobRole, group.operationName]
	);

	return (
		<div
			ref={dropRef}
			role={receiving ? "button" : undefined}
			tabIndex={receiving ? 0 : undefined}
			aria-label={receiving ? `Move the selected block to ${step.title} · ${group.operationName}` : undefined}
			onClick={handoff}
			onKeyDown={onKeyDown}
			style={{
				padding: "6px 8px 8px",
				borderTop: lbb.borderHair,
				borderRadius: 3,
				cursor: selected !== null && droppable ? "copy" : "default",
				background: (selected !== null && droppable) || chipOver ? lbb.dropTint : "transparent",
				...(chipOver ? { outline: `2px solid ${lbb.navy}`, outlineOffset: -2 } : {}),
				...dropSurface(state, (dragging || receiving) && droppable, isOver, canDrop),
			}}
		>
			<div style={{ display: "flex", alignItems: "baseline", gap: 8, paddingBottom: 4 }}>
				<span style={{ ...eyebrowSx, fontSize: 10, color: lbb.ink60 }}>{group.operationName}</span>
				{group.jobRole && (
					<span style={{ fontFamily: lbb.fontMono, fontSize: 11, color: lbb.navy40 }}>{group.jobRole}</span>
				)}
				{group.needed > 0 && (
					<span
						style={{
							fontFamily: lbb.fontMono,
							fontSize: 11,
							fontWeight: 700,
							color: group.have >= group.needed ? lbb.success : lbb.brick,
						}}
					>
						{group.have} of {group.needed}
						<OutCount count={group.outCount} />
					</span>
				)}
				{(dragging || receiving) && droppable && state.caption && (
					<span style={{ fontFamily: lbb.fontUi, fontSize: 11, color: state.outline, marginLeft: "auto" }}>
						{state.caption}
					</span>
				)}
			</div>
			<div style={{ position: "relative", height }}>
				{group.cards.map((card) => (
					<Card
						key={card.id}
						card={card}
						label={`${group.operationName}${card.segmentSeq != null ? ` · part ${card.segmentSeq}` : ""}`}
						clock={clock}
						lane={workerLanes.get(card.workerId) ?? 0}
						selected={selected === card.id}
						moves={moves}
						onSelect={onSelect}
						pinnedUntil={pinEndFor(card.workerId)}
					/>
				))}
				{group.slots.map((slot, index) => (
					<EmptySlot
						key={`slot-${step.workOrderId}-${slot.phaseId}-${slot.startIso}-${index}`}
						jobRole={group.jobRole}
						slot={slot}
						clock={clock}
						lane={workerLanes.size + index}
						onAssign={handleAssign}
					/>
				))}
			</div>
		</div>
	);
};

// ---------------------------------------------------------------------------

const Step: React.FC<{
	step: LaneStep;
	workcenter: string;
	clock: Clock;
	selected: number | null;
	moves: ManualMoves;
	onSelect: (id: number | null) => void;
	onFill: (request: FillRequest) => void;
	gap: MoveGap | null;
	onFillGap: (gap: MoveGap) => void;
	onDismissGap: () => void;
	pinEndFor: PinEndFor;
	/** yyyy-MM-dd of the shown day. */
	dayIso: string;
}> = ({ step, workcenter, clock, selected, moves, onSelect, onFill, gap, onFillGap, onDismissGap, pinEndFor, dayIso }) => {
	const crewColor =
		step.crewNeeded === 0
			? lbb.ink60
			: step.crewHave === 0 || step.crewOut > 0
				? lbb.brick
				: step.crewHave < step.crewNeeded
					? lbb.warning
					: lbb.success;
	const groups = useMemo(() => groupCrewLines(step.phases), [step.phases]);
	const handleFillGap = useCallback(() => {
		if (gap) {
			onFillGap(gap);
		}
	}, [gap, onFillGap]);
	return (
		<div style={{ border: `1px solid ${lbb.navy20}`, borderRadius: 4, background: lbb.paper, overflow: "hidden" }}>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 12,
					padding: "8px 10px",
					background: lbb.alloyWarm,
					fontFamily: lbb.fontUi,
					fontSize: 13,
				}}
			>
				<StepDetails step={step}>
					{(handleDetails) => (
						<button
							type="button"
							onClick={handleDetails}
							aria-label={`${step.title}. Show details`}
							style={{ font: "inherit", fontWeight: 700, color: lbb.navy, background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left" }}
						>
							{step.title}
						</button>
					)}
				</StepDetails>
				<span style={{ fontFamily: lbb.fontMono, fontSize: 11, color: lbb.ink60 }}>
					{hhmm(step.start)} – {hhmm(step.end)} · {fmtHours(step.end - step.start)}
				</span>
				<span
					style={{ marginLeft: "auto", fontFamily: lbb.fontMono, fontSize: 12, fontWeight: 700, color: crewColor }}
					title={`${step.crewHave} of ${step.crewNeeded} crew lines filled${step.crewOut > 0 ? ` · ${step.crewOut} assigned but out` : ""}`}
				>
					{step.crewHave}/{step.crewNeeded}
					<OutCount count={step.crewOut} size={12} />
				</span>
			</div>
			{/* The step's window on the clock, so the eye lines it up with the hour ticks. */}
			<div style={{ position: "relative", height: 4, background: lbb.alloy }}>
				<div
					style={{
						position: "absolute",
						top: 0,
						bottom: 0,
						left: pct(step.start, clock),
						width: widthPct(step.start, step.end, clock),
						background: lbb.navy,
					}}
				/>
			</div>
			{gap && gap.workOrderId === step.workOrderId && (
				<GapPrompt gap={gap} title={step.title} onFill={handleFillGap} onDismiss={onDismissGap} inline />
			)}
			{step.phases.length === 0 && (
				<div style={{ padding: "8px 10px", fontFamily: lbb.fontUi, fontSize: 12, color: lbb.ink60 }}>
					No labor standard matched — nothing to crew on {workcenter}.
				</div>
			)}
			{groups.map((group) => (
				<Group
					dayIso={dayIso}
					workstation={workcenter}
					key={group.key}
					step={step}
					group={group}
					clock={clock}
					selected={selected}
					moves={moves}
					onSelect={onSelect}
					onFill={onFill}
					pinEndFor={pinEndFor}
				/>
			))}
		</div>
	);
};

// ---------------------------------------------------------------------------

/** One work center's lane: its steps, then any cards from another week's steps. */
const Lane: React.FC<{
	row: LaneRow;
	clock: Clock;
	selected: number | null;
	moves: ManualMoves;
	onSelect: (id: number | null) => void;
	onFill: (request: FillRequest) => void;
	onFillGap: (gap: MoveGap) => void;
	pinEnd: PinEndOn;
	/** yyyy-MM-dd of the shown day. */
	dayIso: string;
}> = ({ row, clock, selected, moves, onSelect, onFill, onFillGap, pinEnd, dayIso }) => {
	const pinEndFor = useCallback<PinEndFor>((workerId) => pinEnd(workerId, row.workcenter), [pinEnd, row.workcenter]);
	return (
		<div
			style={{
				display: "grid",
				gridTemplateColumns: LANE_GRID,
				alignItems: "start",
				padding: "12px 24px",
				borderBottom: lbb.borderHair,
				gap: 12,
			}}
		>
			<div style={{ display: "flex", flexDirection: "column", gap: 2, paddingTop: 8 }}>
				<span style={{ fontFamily: lbb.fontUi, fontSize: 15, fontWeight: 600, color: lbb.navy }}>
					{row.workcenter}
				</span>
				<span style={{ fontFamily: lbb.fontMono, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: lbb.ink60 }}>
					{row.steps.length} step{row.steps.length === 1 ? "" : "s"}
					{row.loose.length ? ` · ${row.loose.length} from another week` : ""}
				</span>
			</div>
			<div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
				{row.steps.map((step) => (
					<Step
						dayIso={dayIso}
					key={step.workOrderId}
						step={step}
						workcenter={row.workcenter}
						clock={clock}
						selected={selected}
						moves={moves}
						onSelect={onSelect}
						onFill={onFill}
						gap={moves.gap}
						onFillGap={onFillGap}
						onDismissGap={moves.dismissGap}
						pinEndFor={pinEndFor}
					/>
				))}
				{row.loose.length > 0 && (
					<div style={{ position: "relative", height: row.loose.length * (CARD_HEIGHT + LANE_GAP) }}>
						{row.loose.map((card, index) => (
							<Card
								key={card.id}
								card={card}
								label={card.jobRole}
								clock={clock}
								lane={index}
								selected={selected === card.id}
								moves={moves}
								onSelect={onSelect}
								pinnedUntil={pinEndFor(card.workerId)}
							/>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

const LineView: React.FC<{
	rows: LaneRow[];
	clock: Clock;
	selected: number | null;
	onSelect: (id: number | null) => void;
	moves: ManualMoves;
	onFill: (request: FillRequest) => void;
	onFillGap: (gap: MoveGap) => void;
	/** Standing-pin end date for a worker on a work center on the shown day. */
	pinEnd: PinEndOn;
	/** yyyy-MM-dd of the shown day. */
	dayIso: string;
	/** Everyone on the week's roster with what they do on this day. */
	people: PersonDay[];
	draftedOn?: Map<string, string>;
}> = ({ rows, clock, selected, onSelect, moves, onFill, onFillGap, pinEnd, dayIso, people, draftedOn }) => {
	const span = clock.end - clock.start;
	const ticks = Array.from({ length: span }, (_, i) => clock.start + i);
	const busyRows = rows.filter((row) => row.steps.length || row.loose.length);
	const idleRows = rows.filter((row) => !row.steps.length && !row.loose.length);

	return (
		<div style={{ display: "grid", gridTemplateColumns: `${WORKER_LIST_WIDTH}px minmax(0, 1fr)`, alignItems: "start" }}>
			<PeopleList
				title="People"
				hint="Drop a name on a step to place them for that shift."
				people={people}
				draftedOn={draftedOn}
				emptyText="Nobody is rostered this week."
			/>
			<div style={{ minWidth: 0 }}>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: LANE_GRID,
					alignItems: "center",
					padding: "10px 24px",
					borderBottom: lbb.borderHair,
					...eyebrowSx,
					color: lbb.ink60,
				}}
			>
				<div>Line · steps today</div>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						fontFamily: lbb.fontMono,
						letterSpacing: "0.06em",
					}}
				>
					{ticks.map((h) => (
						<span key={h}>{String(h % 24).padStart(2, "0")}</span>
					))}
				</div>
			</div>

			{busyRows.length === 0 && (
				<div style={{ padding: 48, textAlign: "center", fontFamily: lbb.fontUi, color: lbb.ink60 }}>
					No steps are planned on this day.
				</div>
			)}

			{busyRows.map((row) => (
				<Lane
					dayIso={dayIso}
					key={row.workcenter}
					row={row}
					clock={clock}
					selected={selected}
					moves={moves}
					onSelect={onSelect}
					onFill={onFill}
					onFillGap={onFillGap}
					pinEnd={pinEnd}
				/>
			))}

			{idleRows.length > 0 && (
				<div
					style={{
						padding: "10px 24px",
						fontFamily: lbb.fontUi,
						fontSize: 12,
						color: lbb.ink60,
						borderBottom: lbb.borderHair,
					}}
				>
					Idle today: {idleRows.map((row) => row.workcenter).join(", ")}
				</div>
			)}
			</div>
		</div>
	);
};

export default LineView;
